<template>
  <div>
    <DxForm id="formularioAgente" ref="formInstance" :form-data="model" label-location="top"
      :show-colon-after-label="false" :visible="model != null" validation-group="validacionAgente">
      <!-- xs: 768, sm: 992, md:1200 -->
      <DxColCountByScreen :xs="6" :sm="6" :md="6" :lg="6" />
      
      <!-- Grupo 1: Datos Personales -->
      <DxGroupItem :col-count="6" :col-span="6">
        <DxSimpleItem data-field="Nombre" editor-type="dxTextBox" :col-span="3">
          <DxLabel :text="$t('agentes.form.nombre')" />
          <DxRequiredRule :message="$t('agentes.form.validacion.nombre')" />
        </DxSimpleItem>
        <DxSimpleItem data-field="Apellidos" editor-type="dxTextBox" :col-span="3">
          <DxLabel :text="$t('agentes.form.apellidos')" />
          <DxRequiredRule :message="$t('agentes.form.validacion.apellidos')" />
        </DxSimpleItem>
        <DxSimpleItem data-field="Email" editor-type="dxTextBox" :col-span="3">
          <DxLabel :text="$t('agentes.form.email')" />
          <DxRequiredRule :message="$t('agentes.form.validacion.email')" />
          <DxEmailRule :message="$t('agentes.form.validacion.email_patron')" />
        </DxSimpleItem>
        <DxSimpleItem data-field="Documento" editor-type="dxTextBox" :col-span="3">
          <DxLabel :text="$t('agentes.form.dni_nie')" />
          <DxRequiredRule :message="$t('agentes.form.validacion.dni_nie')" />
          <DxPatternRule :pattern="dniNieNifPattern" :message="$t('agentes.form.validacion.dni_nie_patron')" />
        </DxSimpleItem>
        <DxSimpleItem data-field="Telefono" editor-type="dxTextBox" :col-span="3">
          <DxLabel :text="$t('agentes.form.telefono')" />
          <DxRequiredRule :message="$t('agentes.form.validacion.telefono')" />
          <DxPatternRule :pattern="telefonoPattern" :message="$t('agentes.form.validacion.telefono_patron')" />
        </DxSimpleItem>
        <DxEmptyItem :col-span="3">
        </DxEmptyItem>
      </DxGroupItem>

      <!-- Grupo 2: Dirección -->
      <DxGroupItem :col-count="6" :col-span="6">
        <DxSimpleItem :col-span="6">
          <span class="dx-field-item-label-text text-success">{{
            $t("agentes.form.direccion_fisica")
          }}</span>
        </DxSimpleItem>
        <DxSimpleItem data-field="Direccion.CodigoPostal" editor-type="dxTextBox" :col-span="1">
          <DxLabel :text="$t('agentes.form.codigo_postal')" />
          <DxPatternRule :pattern="codigoPostalPattern" :message="$t('agentes.form.validacion.codigo_postal_patron')" />
        </DxSimpleItem>
        <DxSimpleItem data-field="Direccion.Provincia" editor-type="dxTextBox" :col-span="2">
          <DxLabel :text="$t('agentes.form.provincia')" />
        </DxSimpleItem>
        <DxSimpleItem data-field="Direccion.Municipio" editor-type="dxTextBox" :col-span="3">
          <DxLabel :text="$t('agentes.form.municipio')" />
        </DxSimpleItem>
        <DxSimpleItem data-field="Direccion.TipoVia" editor-type="dxTextBox" :col-span="2">
          <DxLabel :text="$t('agentes.form.tipo_de_via')" />
        </DxSimpleItem>
        <DxSimpleItem data-field="Direccion.NombreVia" editor-type="dxTextBox" :col-span="4">
          <DxLabel :text="$t('agentes.form.nombre_via')" />
        </DxSimpleItem>
        <DxSimpleItem data-field="Direccion.Numero" editor-type="dxTextBox" :col-span="1">
          <DxLabel :text="$t('agentes.form.numero')" />
        </DxSimpleItem>
        <DxSimpleItem data-field="Direccion.Escalera" editor-type="dxTextBox" :col-span="1">
          <DxLabel :text="$t('agentes.form.escalera')" />
        </DxSimpleItem>
        <DxSimpleItem data-field="Direccion.Piso" editor-type="dxTextBox" :col-span="1">
          <DxLabel :text="$t('agentes.form.piso')" />
        </DxSimpleItem>
        <DxSimpleItem data-field="Direccion.Puerta" editor-type="dxTextBox" :col-span="1">
          <DxLabel :text="$t('agentes.form.puerta')" />
        </DxSimpleItem>
      </DxGroupItem>
    </DxForm>
  </div>
</template>

<script setup lang="ts">
import { messageBoxResult } from '@components/dialogs/message-box';
import { type CreateAgenteModel, type DetailAgenteModel, type EditAgenteModel, } from '@api/api';
import { useModalBuilder, type ToolbarReference } from '@helpers/modal-building-helper';
import { type DxButton } from 'devextreme-vue';
import {
  DxEmailRule,
  DxForm,
  DxSimpleItem,
  DxLabel,
  DxColCountByScreen,
  DxRequiredRule,
  DxPatternRule,
  DxEmptyItem,
  DxGroupItem,
} from "devextreme-vue/form";
import { getCurrentInstance, onMounted, reactive, ref } from 'vue';
import { useIntl } from '@services/i18n';
import validationEngine from "devextreme/ui/validation_engine";
import { useApi } from '@api/integration';
import { toastError, toastSuccess } from '@/.shared/services/notification-service';
import { ApiError } from '@/.shared/services/http';
import { UNABLE_TO_CHANGE_USER_TYPE, USER_WITH_DUPLICATED_EMAIL } from '@/.shared/errors/codes';

const api = useApi();
const formInstance = ref<DxForm>();
const intl = useIntl();
const props = defineProps({
  model: {
    type: Object as () => DetailAgenteModel | CreateAgenteModel,
    required: false
  },
  isEditMode: {
    type: Boolean,
    required: true
  }
});
const selectEmpresaAgentesOptions = reactive<any>({
  showClearButton: false
});

const dniNieNifPattern = ref<RegExp>(/(^\d{8}[A-Za-z]$)|(^[A-Za-z]\d{7}[A-Za-z0-9]$)/);
const telefonoPattern = ref<RegExp>(/^\d{9}$/);
const codigoPostalPattern = ref<RegExp>(/^\d{5}$/);

onMounted(async () => {
  const modal = useModalBuilder();
  modal.setContext(getCurrentInstance()!)

  configureButtons(modal);
});

function configureButtons(modal: ReturnType<typeof useModalBuilder>) {
  let okButton: ToolbarReference<DxButton>;
  let cancelButton: ToolbarReference<DxButton>;
  ({ okButton, cancelButton } = modal.standardButtons.addOkCancel());
  okButton.update(w => w.text = `${intl.i18n.t("shared.ui.buttons.save")}`);
  okButton.update(w => w.onClick = async () => {
    saveOrEdit().then((value) => {
      if (value)
        modal.close(messageBoxResult.ok)
    });
  }
  );
  cancelButton.update(w => w.onClick = () => modal.close(messageBoxResult.cancel));
}

const saveOrEdit = async () => {
  var res = validationEngine.validateGroup("validacionAgente");

  if (res.isValid) {
    if (props.isEditMode) {
      try {
        await api.callStandardApi("editAgente", { showLoading: true }, props.model as EditAgenteModel)
          .then(() => toastSuccess(intl.i18n.t('agente.edicion_exito')))
      }
      catch (e: any) {
        if (e instanceof ApiError && e.details?.errorList.length === 1 && [USER_WITH_DUPLICATED_EMAIL].includes(e.details.errorList[0].Code))
          toastError(intl.i18n.t(`shared.errors.api.${e.details.errorList[0].Code}`))
        else throw e;
        return false;
      }
    } else {
      try {
        await api.callStandardApi("createAgente", { showLoading: true }, props.model as CreateAgenteModel)
          .then(() => toastSuccess(intl.i18n.t('agente.creacion_exito')))
      }
      catch (e: any) {
        if (e instanceof ApiError && e.details?.errorList.length === 1 && [USER_WITH_DUPLICATED_EMAIL, UNABLE_TO_CHANGE_USER_TYPE].includes(e.details.errorList[0].Code)){
          toastError(intl.i18n.t(`shared.errors.api.${e.details.errorList[0].Code}`))
        }
        else throw e;
        return false;
      }
    }
    return true;
  } else {
    toastError(intl.i18n.t('agente.revise_validacion'));
  }
}

function validateEmpresaAgentes(e: any) {
  return selectEmpresaAgentesOptions.dataSource.some((option: { Id: any; }) => option.Id === e.value);
}


</script>